import React, { useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { $Input, $InputWithValue } from "us.common/components";

export type PostalCodeInputSearchParams<TValues> = {
    countryCode: string;
    searchText: string;
    formData: TValues;
};

type BaseProps = {
    name: string;
    size?: "small" | "middle" | "large";
    value?: any;
    label?: React.ReactNode;
    required?: boolean;
    disabled?: boolean;
    dataTestid?: string;
    delay?: number;
    useValueInput?: boolean;
    enableSearch?: boolean;
    skipInitialSearch?: boolean;
    minLength?: number;
};

export type PostalCodeInputProps<TValues extends Record<string, any>> =
    BaseProps & {
        countryCodeField?: keyof TValues & string;
        defaultCountryCode?: string;

        onSearch?: (params: PostalCodeInputSearchParams<TValues>) => void;

        onEmpty?: (helpers: {
            setFieldValue: (
                field: string,
                value: any,
                shouldValidate?: boolean
            ) => void;
            setValues: (vals: TValues) => void;
            validateForm: () => Promise<any>;
            values: TValues;
        }) => void;

        onValueChange?: (
            value: string,
            helpers: {
                setFieldValue: (
                    field: string,
                    value: any,
                    shouldValidate?: boolean
                ) => void;
                setValues: (vals: any) => void;
                validateForm: () => Promise<any>;
                values: TValues;
            }
        ) => void;
    };

const PostalCodeInput = <TValues extends Record<string, any>>({
    name,
    size = "small",
    value,
    label,
    required,
    disabled,
    dataTestid,
    delay = 500,
    useValueInput,
    enableSearch = true,
    skipInitialSearch = true,
    minLength = 2,
    countryCodeField,
    defaultCountryCode,
    onSearch,
    onEmpty,
    onValueChange,
}: PostalCodeInputProps<TValues>) => {
    const { values, setFieldValue, setValues, validateForm } =
        useFormikContext<TValues>();

    const [typedValue, setTypedValue] = useState<string>(
        (value ?? (values as any)?.[name] ?? "").toString()
    );

    const typedRef = useRef(false);

    useEffect(() => {
        const externalValue = (value ?? (values as any)?.[name] ?? "").toString();
        if (!typedRef.current) setTypedValue(externalValue);
    }, [value, values, name]);

    if (enableSearch && !onSearch) {
        throw new Error(
            "PostalCodeInput requires `onSearch` when `enableSearch` is true."
        );
    }

    useEffect(() => {
        if (!typedRef.current) return;

        const nextValue = (typedValue ?? "").toString();

        if (skipInitialSearch && nextValue.length === 0) {
            typedRef.current = false;
            return;
        }

        if (nextValue.length === 0) {
            onEmpty?.({ setFieldValue, setValues, validateForm, values });
            typedRef.current = false;
            return;
        }

        if (typeof minLength === "number" && nextValue.length < minLength) return;

        if (!enableSearch) {
            typedRef.current = false;
            return;
        }

        const timeout = setTimeout(() => {
            const countryCode =
                (countryCodeField ? (values as any)?.[countryCodeField] : undefined) ??
                defaultCountryCode ??
                "";

            onSearch?.({ countryCode, searchText: nextValue, formData: values });
            typedRef.current = false;
        }, delay);

        return () => clearTimeout(timeout);
    }, [
        typedValue,
        delay,
        enableSearch,
        skipInitialSearch,
        minLength,
        countryCodeField,
        defaultCountryCode,
        onSearch,
        onEmpty,
        setFieldValue,
        setValues,
        validateForm,
        values,
    ]);

    const InputCmp: any = useValueInput ? $InputWithValue : $Input;

    return (
        <InputCmp
            name={name}
            size={size}
            value={typedValue}
            label={label}
            required={required}
            disabled={disabled}
            dataTestid={dataTestid}
            onChange={(e: any) => {
                const nextValue = e?.target?.value ?? "";
                typedRef.current = true;
                setTypedValue(nextValue);

                setFieldValue(name, nextValue, true);

                onValueChange?.(nextValue, { setFieldValue, setValues, validateForm, values });

                if (nextValue.length === 0) {
                    onEmpty?.({ setFieldValue, setValues, validateForm, values });
                    validateForm();
                    typedRef.current = false;
                }
            }}
        />
    );
};

export default PostalCodeInput;